import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Grid } from "@mui/material";
import { toast } from "react-toastify";
import refbank from "../../../assets/images/refbank.png";
import refwallet from "../../../assets/images/refwallet.png";
import Modal from "../../../components/Modal";
import InputGroup from "../../../components/FormComponent/InputGroup";
import Button from "../../../components/Button";
import { httprequest } from "../../../data/api";

const ReferralBank = ({ dashboardData, setDashboardData, setCurrentView, isLoading }) => {
  const [bankAccount, setBankAccount] = useState(null);
  const [withdrawPoints, setWithdrawPoints] = useState("");
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isEditBankModalOpen, setIsEditBankModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [otpContext, setOtpContext] = useState("add"); // "add" | "edit" | "delete"
  const [otp, setOtp] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_name: "",
    account_number: "",
    bank_name: ""
  });

  useEffect(() => {
    const fetchBankDetails = async () => {
      try {
        const response = await httprequest.get("/api/v1/referrals/referrer/bank-details");
        const data = response?.data?.data;
        if (data && data.id) {
          setBankAccount(data);
        } else {
          setBankAccount(null);
        }
      } catch (error) {
        setBankAccount(null);
      }
    };
    fetchBankDetails();
  }, []);

  const handleBankFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBankForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const initiateWithdraw = () => {
    if (!bankAccount?.id || !withdrawPoints) {
      toast.error("Please add a bank account and enter points to withdraw");

      return;
    }
    setIsWithdrawModalOpen(true);
  };

  const confirmWithdrawPoints = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        bank_detail_id: bankAccount.id,
        points_to_withdraw: Number(withdrawPoints)
      };
      const response = await httprequest.post("/api/v1/referrals/referrer/withdrawals", payload);
      toast.success(response?.data?.message || "Withdrawal successful!");
      setWithdrawPoints("");
      setIsWithdrawModalOpen(false);
      try {
        const res = await httprequest.get("/api/v1/referrals/referrer/overview");
        setDashboardData(res?.data?.data || res?.data);
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Withdrawal failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleAddBankModal = () => {
    setIsBankModalOpen(!isBankModalOpen);
  };

  const handleAddBank = async () => {
    if (!bankForm.bank_name || !bankForm.account_number || !bankForm.account_name) {
      toast.error("Please fill in all required fields");

      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        account_name: bankForm.account_name,
        account_number: bankForm.account_number,
        bank_name: bankForm.bank_name
      };
      const response = await httprequest.post("/api/v1/referrals/referrer/bank-details", payload);
      toast.success(response?.data?.message || "OTP sent to your email");
      setIsBankModalOpen(false);
      setOtpContext("add");
      setIsOtpModalOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add bank account");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      toast.error("Please enter the OTP");

      return;
    }
    setIsSubmitting(true);
    try {
      const payload = { otp };
      const response = await httprequest.post("/api/v1/referrals/referrer/bank-details/verify-otp", payload);
      toast.success(response?.data?.message || "Bank account verified successfully!");
      setIsOtpModalOpen(false);
      setOtp("");
      setBankForm({ account_name: "", account_number: "", bank_name: "" });

      if (otpContext === "delete") {
        setBankAccount(null);
      } else {
        try {
          const bankResponse = await httprequest.get("/api/v1/referrals/referrer/bank-details");
          const data = bankResponse?.data?.data;
          if (data && data.id) setBankAccount(data);
          else setBankAccount(null);
        } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "OTP verification failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await httprequest.post("/api/v1/referrals/referrer/bank-details/resend-otp");
      toast.success(response?.data?.message || "OTP resent successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to resend OTP");
    }
  };

  const handleOpenEditModal = () => {
    setBankForm({
      account_name: bankAccount.account_name || "",
      account_number: bankAccount.account_number || "",
      bank_name: bankAccount.bank_name || ""
    });
    setIsEditBankModalOpen(true);
  };

  const handleEditBank = async () => {
    if (!bankForm.bank_name || !bankForm.account_number || !bankForm.account_name) {
      toast.error("Please fill in all required fields");

      return;
    }
    setIsSubmitting(true);
    try {
      const response = await httprequest.patch(`/api/v1/referrals/referrer/bank-details/${bankAccount.id}`, {
        account_name: bankForm.account_name,
        account_number: bankForm.account_number,
        bank_name: bankForm.bank_name
      });
      toast.success(response?.data?.message || "OTP sent to your email");
      setIsEditBankModalOpen(false);
      setOtpContext("edit");
      setIsOtpModalOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update bank account");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBank = async () => {
    setIsSubmitting(true);
    try {
      const response = await httprequest.delete(`/api/v1/referrals/referrer/bank-details/${bankAccount.id}`);
      toast.success(response?.data?.message || "OTP sent to your email");
      setIsDeleteConfirmModalOpen(false);
      setOtpContext("delete");
      setIsOtpModalOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete bank account");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="ref-dash-main">
      <div className="ref-bank-header">
        <div className="ref-bank-breadcrumb">
          <span className="ref-bank-back" onClick={() => setCurrentView("dashboard")}>
            <Icon icon="bx:chevron-left" /> Dashboard
          </span>
          <span className="ref-bank-current">My Bank</span>
        </div>
        <button className="ref-btn-outline" onClick={() => setCurrentView("withdraw_history")}>Withdraw history</button>
      </div>

      <div className="ref-bank-top-row">
        <div className="ref-bank-stats-grid">
          <div className="ref-stat-card">
            <div className="ref-stat-info">
              <span className="ref-stat-label">Total Referrals</span>
              <span className="ref-stat-value">{isLoading ? "..." : dashboardData?.total_referrals || "—"}</span>
            </div>
            <div className="ref-stat-icon"><Icon icon="fluent:people-community-20-regular" /></div>
          </div>
          <div className="ref-stat-card">
            <div className="ref-stat-info">
              <span className="ref-stat-label">Successful Referrals</span>
              <span className="ref-stat-value">{isLoading ? "..." : dashboardData?.successful_referrals || "—"}</span>
            </div>
            <div className="ref-stat-icon"><Icon icon="fluent:checkmark-circle-20-regular" /></div>
          </div>
          <div className="ref-stat-card">
            <div className="ref-stat-info">
              <span className="ref-stat-label">Points Earned</span>
              <span className="ref-stat-value">{isLoading ? "..." : dashboardData?.points_earned || "—"}</span>
            </div>
            <div className="ref-stat-icon"><Icon icon="ph:coins" /></div>
          </div>
          <div className="ref-stat-card">
            <div className="ref-stat-info">
              <span className="ref-stat-label">Pending Rewards</span>
              <span className="ref-stat-value">{isLoading ? "..." : dashboardData?.pending_points || "—"}</span>
            </div>
            <div className="ref-stat-icon"><Icon icon="fluent:clock-20-regular" /></div>
          </div>
        </div>

        <div className="ref-bank-withdrawal-section">
          <div className="ref-bank-withdrawal-header">
            <div>
              <h2>Withdrawal history</h2>
              <p>See all your withdrawal History</p>
            </div>
            <span className="ref-table-view-all" style={{ cursor: "pointer" }} onClick={() => setCurrentView("withdraw_history")}>View All</span>
          </div>
          <div className="ref-bank-withdrawal-empty">
            <img src={refwallet} alt="Withdrawal History" className="ref-bank-wallet-illustration" />
          </div>
        </div>
      </div>

      <div className="ref-bank-info-section">
        <div className="ref-bank-info-header">
          <h2>Banking Information</h2>
          <p>Add bank accounts used for offline/manual payments and payouts.</p>
        </div>

        <div className="ref-bank-grid">
          <div className="ref-bank-list-col">
            {bankAccount ? (
              <div className="ref-bank-accounts-list">
                <div className="ref-bank-account-card active">
                  <div className="ref-bank-card-top">
                    <div className="ref-bank-card-icon-wrap">
                      <Icon icon="fluent:building-bank-20-filled" />
                    </div>
                    <div className="ref-bank-account-info">
                      <span className="ref-bank-account-bank">{bankAccount.bank_name}</span>
                      {bankAccount.account_name && (
                        <span className="ref-bank-account-name">{bankAccount.account_name}</span>
                      )}
                      <span className="ref-bank-account-number">{bankAccount.account_number}</span>
                    </div>
                    <span className="ref-bank-verified-badge">
                      <Icon icon="fluent:checkmark-circle-20-filled" /> Active
                    </span>
                  </div>
                  <div className="ref-bank-card-actions">
                    <button className="ref-delete-account-btn" onClick={() => setIsDeleteConfirmModalOpen(true)}>
                      <Icon icon="fluent:delete-20-regular" /> Delete
                    </button>
                    <button className="ref-edit-account-btn" onClick={handleOpenEditModal}>
                      <Icon icon="fluent:edit-20-regular" /> Edit
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="ref-bank-empty-state">
                  <img src={refbank} alt="Banking" className="ref-bank-empty-icon" />
                  <p className="ref-bank-empty-text">No Payment Option Added</p>
                </div>
                <button className="ref-bank-add-btn-inline" onClick={toggleAddBankModal} style={{ marginTop: "16px" }}>
                  <Icon icon="bx:plus" /> Add Payment Option
                </button>
              </>
            )}
          </div>

          <div className="ref-withdraw-col">
            <div className="ref-withdraw-card">
              <h3>Withdraw points</h3>
              <div className="ref-withdraw-form">
                <div className="ref-form-group-wrap ref-withdraw-points-group">
                  <div className="ref-withdraw-points-labels">
                    <label>Points to withdraw</label>
                    <span className="ref-available-bal">Available bal: <strong>{dashboardData?.points_earned || 0}</strong></span>
                  </div>
                  <input
                    type="number"
                    placeholder="Enter points"
                    value={withdrawPoints}
                    onChange={(e) => setWithdrawPoints(e.target.value)}
                    className="ref-withdraw-input"
                  />
                </div>
                <button
                  className="ref-btn-primary ref-redeem-btn"
                  onClick={initiateWithdraw}
                  disabled={isSubmitting || !bankAccount || !withdrawPoints}
                >
                  {isSubmitting ? "..." : "Redeem"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <Modal
        isopen={isBankModalOpen}
        closeModal={() => setIsBankModalOpen(false)}
        title="Add a Bank Account"
        subtitle="Add your account for easy withdrawal"
        modalContent={
          <Grid container spacing={2} className="input-padding">
            <Grid item xs={12}>
              <InputGroup
                label="Bank Name"
                required
                inputType="text"
                inputName="bank_name"
                value={bankForm.bank_name}
                onChange={handleBankFormChange}
                placeHolder="e.g. Guarantee Trust Bank"
              />
            </Grid>
            <Grid item xs={12}>
              <InputGroup
                label="Account Name"
                required
                inputType="text"
                inputName="account_name"
                value={bankForm.account_name}
                onChange={handleBankFormChange}
                placeHolder="e.g. John Doe"
              />
            </Grid>
            <Grid item xs={12}>
              <InputGroup
                label="Account Number"
                required
                inputType="text"
                inputName="account_number"
                value={bankForm.account_number}
                onChange={handleBankFormChange}
                placeHolder="e.g. 0123456789"
              />
            </Grid>
          </Grid>
        }
        modalFooter={
          <div className="ref-modal-footer">
            <Button
              buttonName="Cancel"
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              onClick={() => setIsBankModalOpen(false)}
            />
            <Button
              buttonName={isSubmitting ? "Submitting..." : "Add Account"}
              variant="primary"
              buttonSize="full"
              color="btndefault"
              onClick={handleAddBank}
              disabled={isSubmitting}
            />
          </div>
        }
      />

      <Modal
        isopen={isOtpModalOpen}
        closeModal={() => setIsOtpModalOpen(false)}
        title="Verify your bank account"
        subtitle="Enter the OTP sent to your email"
        modalContent={
          <div className="ref-modal-form">
            <InputGroup
              label="OTP"
              required={true}
              inputType="text"
              inputName="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeHolder="Enter OTP"
            />
          </div>
        }
        modalFooter={
          <div className="ref-modal-footer" style={{ display: "flex", flexDirection: "column", width: "100%", gap: "16px" }}>
            <div style={{ width: "100%", textAlign: "left" }}>
              <button type="button" className="ref-resend-otp-btn" onClick={handleResendOtp} style={{ background: "transparent", border: "none", padding: 0, color: "#666", cursor: "pointer", fontSize: "14px" }}>
                Didn't receive code? <span style={{ color: "#0065FF", fontWeight: 500 }}>Resend</span>
              </button>
            </div>
            <Button
              buttonName={isSubmitting ? "Verifying..." : "Verify OTP"}
              variant="primary"
              buttonSize="full"
              color="btndefault"
              onClick={handleVerifyOtp}
              disabled={isSubmitting || !otp}
            />
          </div>
        }
      />

      <Modal
        isopen={isEditBankModalOpen}
        closeModal={() => setIsEditBankModalOpen(false)}
        title="Edit Bank Account"
        subtitle="Update your account details"
        modalContent={
          <Grid container spacing={2} className="input-padding">
            <Grid item xs={12}>
              <InputGroup
                label="Bank Name"
                required
                inputType="text"
                inputName="bank_name"
                value={bankForm.bank_name}
                onChange={handleBankFormChange}
                placeHolder="e.g. Guarantee Trust Bank"
              />
            </Grid>
            <Grid item xs={12}>
              <InputGroup
                label="Account Name"
                required
                inputType="text"
                inputName="account_name"
                value={bankForm.account_name}
                onChange={handleBankFormChange}
                placeHolder="e.g. John Doe"
              />
            </Grid>
            <Grid item xs={12}>
              <InputGroup
                label="Account Number"
                required
                inputType="text"
                inputName="account_number"
                value={bankForm.account_number}
                onChange={handleBankFormChange}
                placeHolder="e.g. 0123456789"
              />
            </Grid>
          </Grid>
        }
        modalFooter={
          <div className="ref-modal-footer">
            <Button
              buttonName="Cancel"
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              onClick={() => setIsEditBankModalOpen(false)}
            />
            <Button
              buttonName={isSubmitting ? "Saving..." : "Save Changes"}
              variant="primary"
              buttonSize="full"
              color="btndefault"
              onClick={handleEditBank}
              disabled={isSubmitting}
            />
          </div>
        }
      />

      <Modal
        isopen={isDeleteConfirmModalOpen}
        closeModal={() => setIsDeleteConfirmModalOpen(false)}
        title="Delete Bank Account"
        subtitle="This action will remove your saved bank account"
        modalContent={
          <div className="ref-modal-form">
            <div style={{ padding: "16px", background: "#FFF5F5", borderRadius: "8px" }}>
              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#666" }}>Bank Name</p>
              <p style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "#1A1A1A" }}>{bankAccount?.bank_name}</p>
              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#666" }}>Account Name</p>
              <p style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "#1A1A1A" }}>{bankAccount?.account_name}</p>
              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#666" }}>Account Number</p>
              <p style={{ margin: "0", fontSize: "16px", fontWeight: "600", color: "#1A1A1A" }}>{bankAccount?.account_number}</p>
            </div>
            <p style={{ marginTop: "16px", fontSize: "14px", color: "#d32f2f" }}>
              An OTP will be sent to your email to confirm this deletion.
            </p>
          </div>
        }
        modalFooter={
          <div className="ref-modal-footer">
            <Button
              buttonName="Cancel"
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              onClick={() => setIsDeleteConfirmModalOpen(false)}
            />
            <Button
              buttonName={isSubmitting ? "Processing..." : "Delete Account"}
              variant="primary"
              buttonSize="full"
              color="btndefault"
              onClick={handleDeleteBank}
              disabled={isSubmitting}
            />
          </div>
        }
      />

      <Modal
        isopen={isWithdrawModalOpen}
        closeModal={() => setIsWithdrawModalOpen(false)}
        title="Confirm Withdrawal"
        subtitle="Please review your withdrawal details"
        modalContent={
          <div className="ref-modal-form">
            <div style={{ padding: "16px", background: "#F8F9FA", borderRadius: "8px", marginBottom: "16px" }}>
              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#666" }}>Bank Name</p>
              <p style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "#1A1A1A" }}>{bankAccount?.bank_name}</p>

              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#666" }}>Account Name</p>
              <p style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "#1A1A1A" }}>{bankAccount?.account_name}</p>

              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#666" }}>Account Number</p>
              <p style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "#1A1A1A" }}>{bankAccount?.account_number}</p>

              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#666" }}>Points to withdraw</p>
              <p style={{ margin: "0", fontSize: "16px", fontWeight: "600", color: "#0065FF" }}>{withdrawPoints}</p>
            </div>
          </div>
        }
        modalFooter={
          <div className="ref-modal-footer">
            <Button
              buttonName="Cancel"
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              onClick={() => setIsWithdrawModalOpen(false)}
            />
            <Button
              buttonName={isSubmitting ? "Processing..." : "Confirm"}
              variant="primary"
              buttonSize="full"
              color="btndefault"
              onClick={confirmWithdrawPoints}
              disabled={isSubmitting}
            />
          </div>
        }
      />
    </main>
  );
};

export default ReferralBank;
